import { useState } from 'react';
import type {
  CandidateDecision,
  CandidateFact,
  CandidatePayload,
  ConflictChoice,
  ImportSession,
  MasterProfile,
} from '../../lib/profile/types';
import {
  applyAccepted,
  detectConflict,
  isResolved,
  payloadFor,
} from '../../lib/profile/merge';
import { Badge, Button, Card, Notice, SectionHeader, controlClass } from '../ui';
import { AlertIcon, CheckIcon } from '../ui/icons';

interface Props {
  session: ImportSession;
  profile: MasterProfile;
  onChange: (session: ImportSession) => void;
  onProfileChange: (profile: MasterProfile) => void;
  onClose: () => void;
}

export function ImportReviewPanel({
  session,
  profile,
  onChange,
  onProfileChange,
  onClose,
}: Props) {
  const [report, setReport] = useState<string>('');

  const updateDecision = (id: string, decision: CandidateDecision) => {
    onChange({ ...session, decisions: { ...session.decisions, [id]: decision } });
  };

  const updateConflict = (id: string, choice: ConflictChoice) => {
    onChange({ ...session, conflicts: { ...session.conflicts, [id]: choice } });
  };

  const updateEdit = (id: string, payload: CandidatePayload) => {
    onChange({ ...session, edits: { ...session.edits, [id]: payload } });
  };

  const applyAll = () => {
    const result = applyAccepted(profile, session);
    onProfileChange(result.profile);
    setReport(
      `Imported ${result.applied.length} of ${session.candidates.length} suggestions. ${result.skipped.length} skipped.`,
    );
  };

  return (
    <div className="space-y-4" id="import-review" role="region" aria-label="Import review">
      <Card>
        <SectionHeader
          title="Review imported facts"
          eyebrow="Import"
          description={`Every item below is a suggestion from “${session.document}”. Accept, edit, or reject each one. Imported facts start as “needs review” and remain unconfirmed until you confirm them.`}
          action={
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close review
            </Button>
          }
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button variant="primary" icon={<CheckIcon />} onClick={applyAll}>
            Apply accepted
          </Button>
          {report ? (
            <span role="status" className="text-sm font-medium text-verified">
              {report}
            </span>
          ) : null}
        </div>
      </Card>

      {session.notes.map((note, i) => (
        <p key={i} className="text-xs text-ink-soft">
          {note}
        </p>
      ))}

      {session.unclassified.length > 0 ? (
        <Card tone="sunken">
          <div className="flex items-center gap-2">
            <AlertIcon className="h-4 w-4 text-gap" />
            <h3 className="text-sm font-semibold text-ink">
              Not imported — could not classify
            </h3>
          </div>
          <ul className="mt-2 list-disc pl-5 text-xs text-ink-soft">
            {session.unclassified.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="space-y-3">
        {session.candidates.map((candidate) => (
          <CandidateRow
            key={candidate.id}
            candidate={candidate}
            session={session}
            profile={profile}
            updateDecision={updateDecision}
            updateConflict={updateConflict}
            updateEdit={updateEdit}
          />
        ))}
      </div>
    </div>
  );
}

function CandidateRow({
  candidate,
  session,
  profile,
  updateDecision,
  updateConflict,
  updateEdit,
}: {
  candidate: CandidateFact;
  session: ImportSession;
  profile: MasterProfile;
  updateDecision: (id: string, d: CandidateDecision) => void;
  updateConflict: (id: string, c: ConflictChoice) => void;
  updateEdit: (id: string, p: CandidatePayload) => void;
}) {
  const payload = payloadFor(session, candidate);
  const conflict = detectConflict(profile, candidate, payload);
  const decision = session.decisions[candidate.id] ?? 'pending';
  const choice = session.conflicts[candidate.id] ?? 'pending';
  const resolved = isResolved(session, candidate.id, profile);

  const decisionTone =
    decision === 'accepted' ? 'verified' : decision === 'rejected' ? 'danger' : 'neutral';
  const decisionLabel =
    decision === 'accepted' ? 'Accept' : decision === 'rejected' ? 'Reject' : 'Pending';

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-ink">{candidate.title}</h4>
          <p className="mt-0.5 text-xs text-ink-faint">
            Source lines {candidate.lines.join(', ') || '—'}
          </p>
        </div>
        <Badge tone={decisionTone as 'verified' | 'danger' | 'neutral'}>{decisionLabel}</Badge>
      </div>

      {candidate.warnings.length > 0 ? (
        <ul className="mt-2 list-disc pl-5 text-xs text-danger">
          {candidate.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null}

      <blockquote className="mt-3 rounded-md border-l-2 border-navy/30 bg-surface-sunken px-3 py-2 text-xs italic text-ink-soft">
        {candidate.evidence || '(no evidence)'}
      </blockquote>

      <PayloadEditor payload={payload} onChange={(p) => updateEdit(candidate.id, p)} />

      {conflict ? (
        <Notice tone="danger">
          <div className="flex flex-col gap-1">
            <p className="font-semibold">Conflict: {conflict.kind}</p>
            <p>
              Existing: <span className="font-medium">{conflict.existing}</span>
            </p>
            <p>
              Proposed: <span className="font-medium">{conflict.proposed}</span>
            </p>
            <p className="mt-1 font-medium">Choose: replace existing or keep existing?</p>
            <div className="mt-1 flex gap-4">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={`conflict-${candidate.id}`}
                  checked={choice === 'replace'}
                  onChange={() => updateConflict(candidate.id, 'replace')}
                />
                Replace
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={`conflict-${candidate.id}`}
                  checked={choice === 'keep'}
                  onChange={() => updateConflict(candidate.id, 'keep')}
                />
                Keep existing
              </label>
            </div>
            {conflict.confirmed ? (
              <p className="mt-1 font-semibold">
                Existing fact is user-confirmed — replacing will discard verified data.
              </p>
            ) : null}
          </div>
        </Notice>
      ) : null}

      <fieldset className="mt-3">
        <legend className="sr-only">Decision for {candidate.title}</legend>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label
            className={`flex cursor-pointer items-center gap-1.5 rounded-pill px-3 py-1.5 font-semibold transition-colors ${
              decision === 'accepted'
                ? 'bg-verified-soft text-verified'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            <input
              type="radio"
              name={`dec-${candidate.id}`}
              checked={decision === 'accepted'}
              onChange={() => updateDecision(candidate.id, 'accepted')}
              disabled={!!conflict && !resolved}
              className="h-4 w-4 text-accent"
            />
            Accept
          </label>
          <label
            className={`flex cursor-pointer items-center gap-1.5 rounded-pill px-3 py-1.5 font-semibold transition-colors ${
              decision === 'rejected'
                ? 'bg-danger-soft text-danger'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            <input
              type="radio"
              name={`dec-${candidate.id}`}
              checked={decision === 'rejected'}
              onChange={() => updateDecision(candidate.id, 'rejected')}
              className="h-4 w-4 text-accent"
            />
            Reject
          </label>
        </div>
      </fieldset>
    </Card>
  );
}

const inputClass = controlClass;

function PayloadEditor({
  payload,
  onChange,
}: {
  payload: CandidatePayload;
  onChange: (p: CandidatePayload) => void;
}) {
  if (payload.kind === 'contact-field') {
    return (
      <label className="mt-3 flex flex-col gap-1 text-sm">
        <span className="font-medium text-ink-soft">Value</span>
        <input
          type="text"
          value={payload.value}
          onChange={(e) =>
            onChange({ kind: 'contact-field', field: payload.field, value: e.target.value })
          }
          className={inputClass}
        />
      </label>
    );
  }
  if (payload.kind === 'contact-link') {
    return (
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink-soft">Label</span>
          <input
            type="text"
            value={payload.label}
            onChange={(e) =>
              onChange({ kind: 'contact-link', label: e.target.value, url: payload.url })
            }
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink-soft">URL</span>
          <input
            type="text"
            value={payload.url}
            onChange={(e) =>
              onChange({ kind: 'contact-link', label: payload.label, url: e.target.value })
            }
            className={inputClass}
          />
        </label>
      </div>
    );
  }
  if (payload.kind === 'summary') {
    return (
      <label className="mt-3 flex flex-col gap-1 text-sm">
        <span className="font-medium text-ink-soft">Summary</span>
        <textarea
          value={payload.value}
          onChange={(e) => onChange({ kind: 'summary', value: e.target.value })}
          className={inputClass}
        />
      </label>
    );
  }
  if (payload.kind === 'skill') {
    return (
      <label className="mt-3 flex flex-col gap-1 text-sm">
        <span className="font-medium text-ink-soft">Skill</span>
        <input
          type="text"
          placeholder="Skill"
          aria-label="Skill"
          value={payload.entry.name}
          onChange={(e) => onChange({ kind: 'skill', entry: { name: e.target.value } })}
          className={inputClass}
        />
      </label>
    );
  }
  if (payload.kind === 'certification') {
    const e = payload.entry;
    return (
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink-soft">Name</span>
          <input
            type="text"
            value={e.name}
            onChange={(ev) =>
              onChange({ kind: 'certification', entry: { ...e, name: ev.target.value } })
            }
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink-soft">Issuer</span>
          <input
            type="text"
            value={e.issuer}
            onChange={(ev) =>
              onChange({ kind: 'certification', entry: { ...e, issuer: ev.target.value } })
            }
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink-soft">Date</span>
          <input
            type="text"
            value={e.date}
            onChange={(ev) =>
              onChange({ kind: 'certification', entry: { ...e, date: ev.target.value } })
            }
            className={inputClass}
          />
        </label>
      </div>
    );
  }
  if (payload.kind === 'experience') {
    const e = payload.entry;
    return (
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <input
          placeholder="Employer"
          value={e.organization}
          onChange={(ev) =>
            onChange({ kind: 'experience', entry: { ...e, organization: ev.target.value } })
          }
          className={inputClass}
        />
        <input
          placeholder="Role"
          value={e.role}
          onChange={(ev) => onChange({ kind: 'experience', entry: { ...e, role: ev.target.value } })}
          className={inputClass}
        />
        <input
          placeholder="Location"
          value={e.location}
          onChange={(ev) =>
            onChange({ kind: 'experience', entry: { ...e, location: ev.target.value } })
          }
          className={inputClass}
        />
      </div>
    );
  }
  if (payload.kind === 'education') {
    const e = payload.entry;
    return (
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <input
          placeholder="Institution"
          value={e.institution}
          onChange={(ev) =>
            onChange({ kind: 'education', entry: { ...e, institution: ev.target.value } })
          }
          className={inputClass}
        />
        <input
          placeholder="Credential"
          value={e.credential}
          onChange={(ev) =>
            onChange({ kind: 'education', entry: { ...e, credential: ev.target.value } })
          }
          className={inputClass}
        />
        <input
          placeholder="Area"
          value={e.area}
          onChange={(ev) => onChange({ kind: 'education', entry: { ...e, area: ev.target.value } })}
          className={inputClass}
        />
      </div>
    );
  }
  if (payload.kind === 'project') {
    const e = payload.entry;
    return (
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <input
          placeholder="Name"
          value={e.name}
          onChange={(ev) => onChange({ kind: 'project', entry: { ...e, name: ev.target.value } })}
          className={inputClass}
        />
        <input
          placeholder="URL"
          value={e.url}
          onChange={(ev) => onChange({ kind: 'project', entry: { ...e, url: ev.target.value } })}
          className={inputClass}
        />
      </div>
    );
  }
  return null;
}
